import clsx from 'clsx';
import { formatCompactCurrency } from '@/lib/format';

export interface CapitalStackSlice {
  label: string;
  amount: number;
  /**
   * Tailwind background token (e.g. `bg-blue`, `bg-purple`, `bg-accent`) —
   * caller picks tokens that align with the design system. Empty string
   * falls back to `bg-text-muted` so the bar still renders.
   *
   * Used as the fallback (e.g. for the simplified `compact={false}` mode).
   * The chunky Lovable-aligned 3-panel mode picks its colors from the slice
   * `tier` field below.
   */
  color: string;
  /**
   * Lovable-aligned 3-panel layout assigns slot colors via this tier hint
   * rather than the Tailwind class. `senior`/`mezz`/`debt` → purple,
   * `equity` → blue, `total` → accent.
   */
  tier?: 'senior' | 'mezz' | 'equity' | 'total';
  /** Optional sub-label (e.g. interest rate or rate type) shown on the chunky panel. */
  sublabel?: string;
}

export interface CapitalStackBarProps {
  slices: CapitalStackSlice[];
  /** Optional explicit total (e.g. computed Total Capitalization) — drives the bottom panel. */
  total?: number;
  /** Caption for the bottom Total panel (default: "Total Capitalization"). */
  totalLabel?: string;
  /**
   * Zone-header title (default: "Capital Stack"). New-development surface
   * passes "Project Sources" to match Lovable's
   * `PublicDealView.tsx:1010` mode='development' branch.
   */
  title?: string;
  className?: string;
}

// Per-tier hex (Lovable's color constants from `src/components/arcanum/colors.ts`).
const TIER_HEX: Record<NonNullable<CapitalStackSlice['tier']>, string> = {
  senior: '#7860a8', // purple @design-token resolves --color-purple
  mezz: '#7860a8', // @design-token resolves --color-purple
  equity: '#3a78b8', // blue @design-token resolves --color-blue
  total: '#00c853', // accent green @design-token resolves --color-accent
};

export function panelStyle(hex: string): React.CSSProperties {
  // Mirrors PublicDealView.tsx:1019-1066 — radial-gradient + glowing border-left.
  return {
    background: `radial-gradient(ellipse at 50% 30%, ${hex}40 0%, ${hex}22 55%, ${hex}10 100%)`,
    border: `1px solid ${hex}55`,
    borderLeft: `3px solid ${hex}aa`,
    boxShadow: `inset 0 0 18px ${hex}1a`,
  };
}

export function textGlowStyle(
  hex: string,
  intensity: '55' | '66' = '55'
): React.CSSProperties {
  return { color: hex, textShadow: `0 0 6px ${hex}${intensity}` };
}

/**
 * Capital Stack visualization — Lovable-parity rewrite (2026-05-12).
 *
 * Mirrors `dashboard-arcanum/src/pages/PublicDealView.tsx:1001-1094` — two
 * chunky 54px-tall radial-gradient panels on top (Debt purple + Equity blue,
 * sized by %), then a full-width Total panel (accent green) below. Each
 * panel: gradient background, 3px-wide glowing left border in the tier color,
 * 1px solid inset shadow for the depth effect, large % label + $-amount.
 *
 * Caller passes `slices: CapitalStackSlice[]` with `tier` hints. Slices
 * keyed by `tier === 'senior'|'mezz'` collapse into the Debt panel
 * (proportional combine). The optional `total` prop drives the bottom
 * panel — falls back to the sum of slices when omitted.
 */
export function CapitalStackBar({
  slices,
  total: totalProp,
  totalLabel = 'Total Capitalization',
  title = 'Capital Stack',
  className,
}: CapitalStackBarProps) {
  const debtSlices = slices.filter(
    (s) => s.tier === 'senior' || s.tier === 'mezz'
  );
  const equitySlice = slices.find((s) => s.tier === 'equity');
  const debtAmount = debtSlices.reduce(
    (sum, s) => sum + (Number.isFinite(s.amount) ? s.amount : 0),
    0
  );
  const equityAmount = Number.isFinite(equitySlice?.amount ?? NaN)
    ? (equitySlice as CapitalStackSlice).amount
    : 0;
  const total = Number.isFinite(totalProp ?? NaN)
    ? (totalProp as number)
    : debtAmount + equityAmount;
  const isEmpty = total <= 0 || (debtAmount <= 0 && equityAmount <= 0);

  const debtPct = total > 0 ? (debtAmount / total) * 100 : 0;
  const equityPct = total > 0 ? (equityAmount / total) * 100 : 0;
  // Defensive: if both debt + equity sum to less than total (e.g. unclassified
  // tranches in the slice list), fold the rest into equity for the visual.
  const equityPctFinal = Math.max(equityPct, 100 - debtPct);
  const debtLabelPrimary =
    debtSlices.length === 1 ? debtSlices[0].label : 'DEBT';
  const debtSubLabel =
    debtSlices.length === 1 && debtSlices[0].sublabel
      ? debtSlices[0].sublabel
      : debtSlices.map((s) => s.label).join(' + ');
  const equityLabel = equitySlice?.label ?? 'Equity';

  if (isEmpty) {
    return (
      <div
        data-testid="capital-stack-bar"
        data-total={total}
        data-empty="true"
        className={clsx('flex flex-col gap-1.5 px-3 py-2', className)}
      >
        <span className="text-[9px] font-mono text-text-dim uppercase tracking-[0.6px]">
          No capital stack data
        </span>
      </div>
    );
  }

  const purpleHex = TIER_HEX.senior;
  const blueHex = TIER_HEX.equity;
  const accentHex = TIER_HEX.total;

  return (
    <div
      data-testid="capital-stack-bar"
      data-total={total}
      data-empty="false"
      className={clsx('flex flex-col gap-[2px] px-3 pt-2 pb-2', className)}
    >
      <div className="flex items-center gap-2.5 mb-1">
        <span
          aria-hidden="true"
          className="w-1 h-1 rounded-full bg-accent shrink-0"
          style={{ boxShadow: '0 0 4px rgba(0,200,83,0.6)' }}
        />
        <h3 className="text-[10px] font-mono uppercase tracking-[2px] text-accent font-bold">
          {title}
        </h3>
        <span className="flex-1 h-px bg-panel-border" />
      </div>
      {/* Top row — Debt + Equity panels, proportional widths. */}
      <div className="flex h-[54px] gap-[2px]">
        <div
          className="flex flex-col justify-between overflow-hidden"
          style={{
            width: `${debtPct}%`,
            padding: '6px 9px',
            ...panelStyle(purpleHex),
          }}
        >
          <div className="flex items-baseline justify-between gap-2">
            <span
              className="font-mono text-[9px] font-bold tracking-[1.5px] uppercase"
              style={textGlowStyle(purpleHex, '66')}
            >
              {debtLabelPrimary}
            </span>
            <span
              className="font-mono text-[15px] font-bold tabular-nums tracking-[-0.3px]"
              style={textGlowStyle(purpleHex, '55')}
            >
              {debtPct.toFixed(1)}%
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-2 min-w-0">
            <span
              className="font-mono text-[8px] tracking-[0.4px] truncate"
              style={{ color: 'rgba(255,255,255,0.75)' }}
            >
              {debtSubLabel || debtLabelPrimary}
            </span>
            <span
              className="font-mono text-[10.5px] font-semibold tabular-nums"
              style={textGlowStyle(purpleHex, '55')}
            >
              {formatCompactCurrency(debtAmount)}
            </span>
          </div>
        </div>
        <div
          className="flex flex-col justify-between overflow-hidden"
          style={{
            width: `${equityPctFinal}%`,
            padding: '6px 9px',
            ...panelStyle(blueHex),
          }}
        >
          <div className="flex items-baseline justify-between gap-2">
            <span
              className="font-mono text-[9px] font-bold tracking-[1.5px] uppercase"
              style={textGlowStyle(blueHex, '66')}
            >
              EQUITY
            </span>
            <span
              className="font-mono text-[15px] font-bold tabular-nums tracking-[-0.3px]"
              style={textGlowStyle(blueHex, '55')}
            >
              {equityPctFinal.toFixed(1)}%
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-2 min-w-0">
            <span
              className="font-mono text-[8px] tracking-[0.4px] truncate"
              style={{ color: 'rgba(255,255,255,0.75)' }}
            >
              {equityLabel}
            </span>
            <span
              className="font-mono text-[10.5px] font-semibold tabular-nums"
              style={textGlowStyle(blueHex, '55')}
            >
              {formatCompactCurrency(equityAmount)}
            </span>
          </div>
        </div>
      </div>

      {/* Bottom panel — Total full-width. */}
      <div
        className="flex flex-col justify-between"
        style={{
          height: '54px',
          padding: '6px 10px',
          ...panelStyle(accentHex),
        }}
      >
        <div className="flex items-baseline justify-between gap-2">
          <span
            className="font-mono text-[9px] font-bold tracking-[1.5px] uppercase"
            style={textGlowStyle(accentHex, '66')}
          >
            {totalLabel.toUpperCase()}
          </span>
          <span
            className="font-mono text-[15px] font-bold tabular-nums tracking-[-0.3px]"
            style={textGlowStyle(accentHex, '55')}
          >
            100.0%
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span
            className="font-mono text-[8px] tracking-[0.4px]"
            style={{ color: 'rgba(255,255,255,0.75)' }}
          >
            All-in capital basis
          </span>
          <span
            className="font-mono text-[12px] font-bold tabular-nums"
            style={textGlowStyle(accentHex, '55')}
          >
            {formatCompactCurrency(total)}
          </span>
        </div>
      </div>
    </div>
  );
}
